import { useEffect, useMemo, useRef, useState } from 'react'
import { LockedValue, PlanLockedPill } from '../../../components/billing/RoLocked'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Search,
  Upload,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Inbox,
  CheckCircle2,
  FileText,
  Download,
  X,
  Clock,
  Plus
} from 'lucide-react'
import { EstimateLockIcon } from '../EstimateLockIcon'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { MobileSortControl, SortableTableHead, nextSort, sortFromQuery, writeSortToQuery } from '@components/table-sort'
import { Checkbox } from '@components/ui/checkbox'
import { KpiCard, UploadEstimateModal, BulkQuoteModal } from '@components/adas-intelligence'
import { useAdasIntelligenceStore } from '@caradasstore/AdasIntelligenceStore'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { useClientAndInsuranceData } from '@hooks/useClientAndInsuranceData'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { useModuleAccess } from '@hooks/useModuleAccess'
import { AdasIntelligenceHeader, AdasIntelligenceTabs } from '../tabs'
import { describeVehicleInProgress } from '../adapters'
import { buildAdasReportFilename, downloadScrubEstimatePdf } from '../../../api/scrubApi'
import { requestBulkCreateQuotes } from '../../../api/api'
import { fuzzyMatchClients, fuzzyMatchInsurers, matchWithFuzzy } from '../../../utils/fuzzyMatchClients'
import { FormModal, FormModalFooter } from '@components/branded-modal'

const ANY = '__any__'

const DATE_FILTER_OPTIONS = [
  { value: '24h', label: 'Last 24h' },
  { value: '48h', label: 'Last 48h' },
  { value: '7d', label: 'Last 7d' },
  { value: '30d', label: 'Last 30d' },
  { value: 'all', label: 'All' }
]

/** Per BE contract: send dateFrom only (YYYY-MM-DD UTC), omit dateTo so the
 *  window runs from 00:00 UTC of (today − offset) up to "now". Offsets here
 *  span exactly N calendar days *including* today:
 *    24h → today − 1, 48h → today − 2, 7d → today − 6, 30d → today − 29. */
const PRESET_DAY_OFFSET = { '24h': 1, '48h': 2, '7d': 6, '30d': 29 }

const computeDateRange = (filter) => {
  const offset = PRESET_DAY_OFFSET[filter]
  if (offset == null) return null
  const now = new Date()
  const utcToday = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const from = new Date(utcToday - offset * 24 * 60 * 60 * 1000)
  return { dateFrom: from.toISOString().slice(0, 10), dateTo: '' }
}

/** Format an ISO date as "May 4, 2026 7:43 PM" — short month, no comma between year and time. */
const formatDateTime = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${date} ${time}`
}

const RecordLink = ({ record, autoQuote, onNavigate }) => {
  if (!record?.number) {
    // No record yet: say whether the queue still owes an auto quote, or gave up on one.
    if (autoQuote?.inProgress) {
      return (
        <span className='inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1F6FEB]' data-testid='auto-quoting'>
          <Loader2 className='h-3.5 w-3.5 animate-spin' />
          Auto-quoting…
        </span>
      )
    }
    if (autoQuote?.vehicleInProgress) {
      return (
        <span
          className='text-[13px] font-medium text-[#B45309]'
          title={autoQuote.detail || "This vehicle's quote, work order or invoice is still in progress."}
          data-testid='vehicle-in-progress'
        >
          Vehicle in progress
        </span>
      )
    }
    if (autoQuote?.status === 'FAILED') {
      return (
        <span className='text-[13px] font-medium text-[#B91C1C]' title={autoQuote.error || 'The automatic quote could not be created.'}>
          Auto-quote failed
        </span>
      )
    }
    return <span className='text-[13px] text-[#6B7280]'>—</span>
  }
  const label = record.type ? `${record.type} #${record.number}` : `#${record.number}`
  if (!record.href) return <span className='text-[13px] font-medium text-[#1F6FEB]'>{label}</span>
  return (
    <button
      type='button'
      onClick={(evt) => {
        evt.stopPropagation()
        onNavigate(record.href)
      }}
      className='text-[13px] font-medium text-[#1F6FEB] hover:underline'
    >
      {label}
    </button>
  )
}

const CalibrationsCell = ({ count }) => {
  if (count == null) return <span className='text-[13px] text-[#6B7280]'>—</span>
  return <span className='text-[13px] text-[#0A1628]'>{count}</span>
}

const StatusPill = ({ status }) => {
  const palette = {
    new: '#2C3D4D',
    reviewed: '#2C3D4D',
    quoted: '#2C3D4D',
    'quote sent': '#2C3D4D',
    processing: '#D97706',
    failed: '#E24B4A'
  }
  const color = palette[status] || '#6B7280'
  return (
    <div className='inline-flex items-center gap-1.5'>
      <span className='inline-block h-2 w-2 rounded-full' style={{ background: color }} />
      <span className='text-[12px] font-medium capitalize' style={{ color }}>
        {status}
      </span>
    </div>
  )
}

// Sortable columns, in table order (keys whitelisted by the backend, ScrubEstimateSpecification).
// Record sorts by the linked quote / work order / invoice number; unlinked estimates last.
const ESTIMATE_SORT_OPTIONS = [
  { value: 'roNumber', label: 'RO#' },
  { value: 'client', label: 'Client' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'vin', label: 'VIN' },
  { value: 'dateReceived', label: 'Date Received' },
  { value: 'status', label: 'Status' },
  { value: 'calibrations', label: 'Calibrations' },
  { value: 'record', label: 'Record' }
]
const ESTIMATE_SORT_KEYS = ESTIMATE_SORT_OPTIONS.map((o) => o.value)

export const QueuePage = () => {
  const history = useHistory()
  const location = useLocation()
  const estimates = useAdasIntelligenceStore((s) => s.estimates)
  const page = useAdasIntelligenceStore((s) => s.page)
  const totalElements = useAdasIntelligenceStore((s) => s.totalElements)
  const totalPages = useAdasIntelligenceStore((s) => s.totalPages)
  const size = useAdasIntelligenceStore((s) => s.size)
  const isListLoading = useAdasIntelligenceStore((s) => s.isListLoading)
  const listError = useAdasIntelligenceStore((s) => s.listError)
  const filters = useAdasIntelligenceStore((s) => s.filters)
  const setFilters = useAdasIntelligenceStore((s) => s.setFilters)
  const sort = useAdasIntelligenceStore((s) => s.sort)
  const setSort = useAdasIntelligenceStore((s) => s.setSort)
  const setPage = useAdasIntelligenceStore((s) => s.setPage)
  const resetFilters = useAdasIntelligenceStore((s) => s.resetFilters)
  const loadEstimates = useAdasIntelligenceStore((s) => s.loadEstimates)
  const loadDetail = useAdasIntelligenceStore((s) => s.loadDetail)
  const getDetail = useAdasIntelligenceStore((s) => s.getDetail)
  const markReviewed = useAdasIntelligenceStore((s) => s.markReviewed)
  const kpiCounts = useAdasIntelligenceStore((s) => s.kpiCounts)
  const loadKpiCounts = useAdasIntelligenceStore((s) => s.loadKpiCounts)
  const ccId = useCenterStore((store) => store.ccId)
  const { clients, insuranceCompanies } = useClientAndInsuranceData(ccId)

  const [uploadOpen, setUploadOpen] = useState(false)
  // Uploading an estimate and creating quotes from one are both writes.
  const isReadOnly = useIsReadOnly()
  // Turning estimates into quotes lands in Quotes, which the AI Report plan lacks (CAR-3710).
  const canUse = useModuleAccess()
  // A client login keeps the `quotes` module (it reads its quotes) but cannot create one (CAR-4055).
  const canCreateQuotes = !isReadOnly && canUse('quotes')
  // A client cannot upload, so its empty list doesn't ask it to (CAR-4055).
  const emptyListText = isReadOnly ? 'No AI Reports yet.' : 'No estimates found. Upload one to get started.'
  const [bulkQuoteOpen, setBulkQuoteOpen] = useState(false)
  const [isCreatingBulk, setIsCreatingBulk] = useState(false)
  // Estimate details for the current bulk selection, keyed by estimate id. Fetched when the modal
  // opens (not on confirm) because the modal needs the resolved client/insurance to pre-select its
  // dropdowns, and the payload needs the structured vehicle off the same DTO.
  const [bulkDetails, setBulkDetails] = useState({})
  const [isPreparingBulk, setIsPreparingBulk] = useState(false)
  const [bulkQuoteError, setBulkQuoteError] = useState(null)
  // Error dialog: shown when the bulk API rejects entirely or when any per-row results
  // come back with success=false (typically "Client not found" / "Insurance company not found").
  const [errorDialog, setErrorDialog] = useState(null) // { title, message, items: [{ ro, message }] } | null
  const [isBulkDownloading, setIsBulkDownloading] = useState(false)
  const [bulkDownloadError, setBulkDownloadError] = useState(null)
  const [isBulkMarkingReviewed, setIsBulkMarkingReviewed] = useState(false)
  const [bulkMarkReviewedError, setBulkMarkReviewedError] = useState(null)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  // URL is the source of truth for filters / pagination / date chip so that
  // goBack from an estimate detail restores the same view, and a shared link
  // reproduces it. We snapshot the URL params once on mount; the URL sync
  // effect below keeps them in step with state changes.
  const initialUrlParamsRef = useRef(null)
  if (initialUrlParamsRef.current === null) {
    initialUrlParamsRef.current = new URLSearchParams(location.search)
  }
  const getInitial = (key, fallback) => initialUrlParamsRef.current.get(key) ?? fallback
  const [searchInput, setSearchInput] = useState(getInitial('q', filters.search || ''))
  const [dateFilter, setDateFilter] = useState(getInitial('chip', '7d'))

  const searchOpen = useMobileToolbarStore((s) => s.searchOpen)
  const filterOpen = useMobileToolbarStore((s) => s.filterOpen)
  const closeMobileToolbar = useMobileToolbarStore((s) => s.closeAll)
  useEffect(() => () => closeMobileToolbar(), [closeMobileToolbar])
  // Clear the queue filters on unmount so navigating to the other ADAS
  // Intelligence page (Reports) — or re-entering from elsewhere — starts from a
  // clean, unfiltered list. Returning via the browser Back button re-seeds the
  // exact view from the URL, so the estimate-detail round-trip is unaffected.
  useEffect(() => () => resetFilters(), [resetFilters])
  // Local state for the mobile filter drawer (committed to store on Apply)
  const [draftClient, setDraftClient] = useState(filters.client || '')
  const [draftStatus, setDraftStatus] = useState(filters.status || '')
  const [draftDate, setDraftDate] = useState(dateFilter)
  const [draftSort, setDraftSort] = useState(sort)
  useEffect(() => {
    if (filterOpen) {
      setDraftClient(filters.client || '')
      setDraftStatus(filters.status || '')
      setDraftDate(dateFilter)
      setDraftSort(sort)
    }
  }, [filterOpen, filters.client, filters.status, dateFilter, sort])
  // Centralised so chip + mobile drawer both push the date range to the store.
  // Empty dateFrom/dateTo clears any prior range so the next call hits the BE
  // without date params.
  const applyDateFilter = (value) => {
    setDateFilter(value)
    setSearchInput('')
    const range = computeDateRange(value)
    setFilters({ search: '', dateFrom: range?.dateFrom || '', dateTo: range?.dateTo || '' })
  }
  const applyMobileFilters = () => {
    setFilters({ client: draftClient, status: draftStatus })
    if (draftSort?.key !== sort?.key || draftSort?.dir !== sort?.dir) setSort(draftSort)
    applyDateFilter(draftDate)
    closeMobileToolbar()
  }
  const clearMobileFilters = () => {
    setDraftClient('')
    setDraftStatus('')
    setDraftDate('all')
    setDraftSort(null)
  }

  // On mount: seed the store filters + page + date range from the URL so the
  // first list + KPI fetches narrow exactly to what the URL describes.
  const didInitFromUrlRef = useRef(false)
  useEffect(() => {
    if (didInitFromUrlRef.current) return
    didInitFromUrlRef.current = true
    const p = initialUrlParamsRef.current
    const range = computeDateRange(dateFilter)
    const urlClient = p.get('client') || ''
    const urlStatus = p.get('status') || ''
    const urlSearch = p.get('q') || ''
    const urlPage = Math.max(0, Number(p.get('page') || 0))
    setFilters({
      search: urlSearch,
      client: urlClient,
      status: urlStatus,
      dateFrom: range?.dateFrom || '',
      dateTo: range?.dateTo || ''
    })
    setSort(sortFromQuery(p, ESTIMATE_SORT_KEYS))
    if (urlPage > 0) setPage(urlPage)
  }, [dateFilter, setFilters, setSort, setPage])

  // Debounce search input into the store filter
  useEffect(() => {
    const h = setTimeout(() => {
      if (searchInput !== filters.search) setFilters({ search: searchInput })
    }, 350)
    return () => clearTimeout(h)
  }, [searchInput, filters.search, setFilters])

  // Mirror filters / page / date chip into the URL via history.replace so
  // history.goBack from an estimate detail restores the exact list view, and
  // a shared link reproduces it.
  useEffect(() => {
    const params = new URLSearchParams()
    if (filters.search) params.set('q', filters.search)
    if (filters.client) params.set('client', filters.client)
    if (filters.status) params.set('status', filters.status)
    if (dateFilter && dateFilter !== '7d') params.set('chip', dateFilter)
    if (page > 0) params.set('page', String(page))
    writeSortToQuery(params, sort)
    const next = params.toString()
    const current = location.search.replace(/^\?/, '')
    if (next !== current) {
      history.replace({ pathname: window.location.pathname, search: next ? `?${next}` : '' })
    }
  }, [filters.search, filters.client, filters.status, dateFilter, page, sort, history, location.search])

  // Reload whenever page, filters, or active center change (and on mount)
  useEffect(() => {
    loadEstimates()
  }, [page, filters, sort, ccId, loadEstimates])

  // KPI totals are scoped to the active server-side filters (search + client) and the
  // active calibration center. Re-run whenever those change so the tiles always match
  // the queue. Upload / mark-reviewed / mark-quote-sent actions also trigger this from
  // the store.
  useEffect(() => {
    loadKpiCounts()
  }, [filters, ccId, loadKpiCounts])

  // Client dropdown options come from the full clients list for the active center
  // (via useClientAndInsuranceData) so users can filter by any client they own,
  // not just clients that happen to appear on the current loaded page. Estimates
  // are added in to surface any names that exist in data but not in the registry.
  const clientOptions = useMemo(() => {
    const set = new Set()
    ;(clients || []).forEach((c) => {
      if (c?.name) set.add(c.name)
    })
    estimates.forEach((e) => {
      if (e.client && e.client !== '—') set.add(e.client)
    })
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [clients, estimates])

  // The list (incl. pagination & KPI counts) is now narrowed by the BE via dateFrom /
  // dateTo, so visibleEstimates is just the loaded page as-is.
  const visibleEstimates = estimates

  // While the queue still owes a quote for any visible row, re-read the page every few seconds so
  // the spinner turns into the Q# link without a manual refresh (the debounce is ~30s).
  const anyAutoQuoting = visibleEstimates.some((e) => e.autoQuote?.inProgress && !e.record?.number)
  useEffect(() => {
    if (!anyAutoQuoting) return undefined
    const timer = setInterval(() => loadEstimates(), 5000)
    return () => clearInterval(timer)
  }, [anyAutoQuoting, loadEstimates])

  // Text search should never move the KPI tiles — keep the last non-search snapshot
  // and render that whenever filters.search is active.
  const lastNonSearchKpiRef = useRef(kpiCounts)
  useEffect(() => {
    if (!filters.search) lastNonSearchKpiRef.current = kpiCounts
  }, [filters.search, kpiCounts])
  const renderKpiCounts = filters.search ? lastNonSearchKpiRef.current : kpiCounts

  const toggleRow = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const clearSelection = () => setSelectedIds(new Set())

  const selectedEstimates = useMemo(() => estimates.filter((e) => e.id && selectedIds.has(e.id)), [estimates, selectedIds])

  // Load the detail DTOs for the current selection as soon as the modal opens, so the client /
  // insurance dropdowns can be pre-selected from what the backend resolved instead of the user
  // discovering the gaps only after a failed submit. Cached details are reused.
  useEffect(() => {
    if (!bulkQuoteOpen) {
      setBulkDetails({})
      setIsPreparingBulk(false)
      return undefined
    }
    const ids = selectedEstimates.map((e) => e.id).filter(Boolean)
    if (ids.length === 0) return undefined
    let cancelled = false
    setIsPreparingBulk(true)
    ;(async () => {
      const loaded = await Promise.all(ids.map(async (id) => [id, getDetail(id) || (await loadDetail(id))]))
      if (cancelled) return
      setBulkDetails(Object.fromEntries(loaded.filter(([, detail]) => detail)))
      setIsPreparingBulk(false)
    })()
    return () => {
      cancelled = true
      setIsPreparingBulk(false)
    }
    // The selection is frozen while the modal is open, so opening it is the only trigger.
  }, [bulkQuoteOpen])

  // estimateId → { clientId, insuranceCompanyId }: the backend-resolved ids, falling back to a
  // name match against the lookup lists. Null where nothing resolves confidently — the modal then
  // shows the extracted name as a placeholder and requires the user to pick.
  const bulkDefaults = useMemo(() => {
    const out = {}
    selectedEstimates.forEach((e) => {
      const detail = bulkDetails[e.id]
      if (!detail) return
      out[e.id] = {
        clientId: detail.clientId ?? matchWithFuzzy(clients, detail.client, fuzzyMatchClients)?.id ?? null,
        insuranceCompanyId:
          detail.insuranceCompanyId ?? matchWithFuzzy(insuranceCompanies, detail.insurance, fuzzyMatchInsurers)?.id ?? null
      }
    })
    return out
  }, [selectedEstimates, bulkDetails, clients, insuranceCompanies])

  const onBulkCreateQuotes = async (quotable, assignments = {}) => {
    if (!ccId || !quotable?.length) return
    setIsCreatingBulk(true)
    setBulkQuoteError(null)
    try {
      // A row whose detail never loaded has no vehicle to send, so it cannot become a quote.
      // Collect those instead of dropping them silently — otherwise the user sees "Created 3"
      // for a selection of 4 with nothing said about the fourth.
      const unprepared = []
      const sent = []
      quotable.forEach((row) => {
        const detail = bulkDetails[row.id]
        if (!detail) {
          unprepared.push({ ro: row.ro || '—', message: 'Estimate details could not be loaded — try again.' })
          return
        }
        const vin = detail.rawVehicle || {}
        sent.push({
          row,
          quote: {
            calibration_center_id: ccId,
            // The modal owns these two: it seeds them from bulkDefaults and blocks Create until
            // every row has both, so anything reaching here is an explicit, confirmed choice.
            client_id: assignments[row.id]?.clientId ?? null,
            insurance_company_id: assignments[row.id]?.insuranceCompanyId ?? null,
            repair_order_num: row.ro,
            type: 'STANDARD',
            // Same two fields New Quote sends when launched from an estimate: they put the quote on
            // the auto-quote path, where the BE maps the estimate's engine cards to AUTO_QUOTE
            // calibration / service lines. Without them the BE builds a plain STANDARD quote with
            // none of the scrubbed findings and no pricing off them.
            source: 'ADAS_INTELLIGENCE',
            scrub_estimate_id: row.id,
            vehicle: {
              vin: vin.vin || row.vin || '',
              year: vin.year || '',
              make: vin.make || '',
              model: vin.model || '',
              trim: vin.trim || '',
              exterior_color: vin.color || '',
              // CAR-3896: without the tank capacity the vehicle saved with 0 — read as "no fuel
              // tank", which locked pre-calibration's fuel level. The BE also back-fills it from
              // the estimate, so this just keeps the request complete.
              fuel_capacity_gal: vin.fuel_capacity_gal || 0,
              image_url: vin.image_url || '',
              calibration_center_id: ccId
            }
          }
        })
      })
      const quotes = sent.map((entry) => entry.quote)

      if (quotes.length === 0) {
        setBulkQuoteOpen(false)
        setErrorDialog({
          title: 'Cannot create quotes',
          message: 'None of the selected estimates could be prepared.',
          items: unprepared
        })
        return
      }

      const result = await requestBulkCreateQuotes({ data: { quotes } })
      const created = result?.totalCreated ?? 0
      const failed = result?.totalFailed ?? 0
      const failures = (result?.results || []).filter((r) => !r.success)

      // The BE links each estimate to its new quote in the same transaction (scrub_estimate_id), so
      // the Records column picks it up on the reload below with no follow-up call per row.

      // Refresh whether or not all succeeded so the new Quote records show up
      if (created > 0) {
        loadEstimates()
        loadKpiCounts()
      }

      if (failed === 0 && created > 0 && unprepared.length === 0) {
        // Full success — close, clear, and route to the quotes list.
        setBulkQuoteOpen(false)
        clearSelection()
        history.push('/quotes')
      } else {
        // Any failure (full or partial) — close the bulk modal and open the error dialog
        // so the user gets a clear summary of what didn't get created.
        setBulkQuoteOpen(false)
        setErrorDialog({
          title: created > 0 ? 'Some quotes were not created' : 'Failed to create quotes',
          message:
            created > 0
              ? `Created ${created} of ${quotable.length}. The following could not be created:`
              : 'None of the selected estimates could be turned into quotes.',
          items: [
            ...failures.map((r) => ({
              ro: sent[r.index]?.row?.ro || `#${r.index + 1}`,
              // QUOTE_ALREADY_EXISTS: the vehicle's quote, work order or invoice (any RO) is still open; name it.
              message: (r.errorCode === 'QUOTE_ALREADY_EXISTS' && describeVehicleInProgress(r.existingQuote)) || r.errorMessage || 'Failed'
            })),
            ...unprepared
          ]
        })
      }
    } catch (err) {
      console.error('Bulk quote create failed', err)
      setBulkQuoteOpen(false)
      setErrorDialog({
        title: 'Failed to create quotes',
        message: err?.message || 'The bulk quote request did not complete.',
        items: []
      })
    } finally {
      setIsCreatingBulk(false)
    }
  }

  const onBulkMarkReviewed = async () => {
    if (selectedEstimates.length === 0) return
    setIsBulkMarkingReviewed(true)
    setBulkMarkReviewedError(null)
    try {
      // Skip rows that are already reviewed (or have moved further to quote-sent) so we don't
      // hammer the BE with no-op PATCHes. Sequential to keep the order of optimistic updates.
      const targets = selectedEstimates.filter((e) => e.id && e.status !== 'reviewed' && e.status !== 'quote sent')
      for (const row of targets) {
        // eslint-disable-next-line no-await-in-loop
        await markReviewed(row.id)
      }
      clearSelection()
    } catch (err) {
      console.error('Bulk mark-reviewed failed', err)
      setBulkMarkReviewedError(err?.message || 'Failed to mark one or more estimates as reviewed')
    } finally {
      setIsBulkMarkingReviewed(false)
    }
  }

  const onBulkDownload = async () => {
    if (selectedEstimates.length === 0) return
    setIsBulkDownloading(true)
    setBulkDownloadError(null)
    try {
      for (const row of selectedEstimates) {
        if (!row?.id) continue
        // Sequential so the browser can surface each download dialog in order
        // eslint-disable-next-line no-await-in-loop
        await downloadScrubEstimatePdf(row.id, {
          filename: buildAdasReportFilename({ make: row.make, ro: row.ro, vin: row.vin })
        })
      }
    } catch (err) {
      console.error('Bulk PDF download failed', err)
      setBulkDownloadError(err?.message || 'Failed to download one or more PDFs')
    } finally {
      setIsBulkDownloading(false)
    }
  }

  // Mobile status tabs ↔ store filter mapping
  const mobileStatusTabs = [
    { key: '', label: 'All', count: totalElements },
    { key: 'NEW', label: 'New', count: kpiCounts.newCount ?? 0 },
    { key: 'REVIEWED', label: 'Reviewed', count: kpiCounts.reviewedCount ?? 0 },
    { key: 'QUOTE_SENT', label: 'Quoted', count: kpiCounts.quoteSentCount ?? 0 }
  ]

  const statusPillStyle = (status) => {
    switch (status) {
      case 'new':
        return { bg: 'bg-[#E8EFFF]', dot: 'bg-[#2C3D4D]', text: 'text-[#2C3D4D]' }
      case 'reviewed':
        return { bg: 'bg-[#E8EFFF]', dot: 'bg-[#2C3D4D]', text: 'text-[#2C3D4D]' }
      case 'quoted':
      case 'quote sent':
        return { bg: 'bg-[#E8EFFF]', dot: 'bg-[#2C3D4D]', text: 'text-[#2C3D4D]' }
      case 'failed':
        return { bg: 'bg-[#FEE2E2]', dot: 'bg-[#E24B4A]', text: 'text-[#E24B4A]' }
      default:
        return { bg: 'bg-[#EBEDF0]', dot: 'bg-[#6B7280]', text: 'text-[#6B7280]' }
    }
  }

  return (
    <div className='mx-auto max-w-[1400px] py-6'>
      {/* Desktop header + tabs (untouched) */}
      <div className='hidden min-[435px]:block'>
        <AdasIntelligenceHeader
          right={
            isReadOnly ? null : (
              <button
                onClick={() => setUploadOpen(true)}
                className='inline-flex items-center gap-1.5 px-5 py-2.5 bg-sidebar text-white text-sm font-medium rounded-lg hover:bg-sidebar/90 transition-colors'
              >
                <Upload className='h-4 w-4' /> Upload Estimate
              </button>
            )
          }
        />
        <AdasIntelligenceTabs />
      </div>

      {/* Mobile search — toggled from the global header search icon */}
      {searchOpen && (
        <div className='min-[435px]:hidden mb-4 flex items-center gap-2 rounded-lg bg-[#F4F6F9] px-3 py-2.5'>
          <Search className='h-4 w-4 text-[#6B7280]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            autoFocus
            placeholder='Search RO#, VIN, Vehicle or Client...'
            className='flex-1 border-0 bg-transparent text-[14px] text-[#0A1628] placeholder:text-[#6B7280] focus:outline-none'
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              aria-label='Clear search'
              className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#475569]'
            >
              <X className='h-3 w-3' />
            </button>
          )}
        </div>
      )}

      {/* Mobile status tabs with counts */}
      <div className='min-[435px]:hidden mb-4 flex items-center gap-5 overflow-x-auto border-b border-[#D1D5D8] pb-0'>
        {mobileStatusTabs.map((t) => {
          const active = (filters.status || '') === t.key
          return (
            <button
              key={t.key || 'all'}
              type='button'
              onClick={() => setFilters({ status: t.key })}
              className={`pb-3 -mb-px flex items-center gap-2 whitespace-nowrap text-[14px] font-medium transition-colors ${
                active ? 'text-[#1F6FEB] border-b-2 border-[#1F6FEB]' : 'text-[#6B7280] hover:text-[#475569]'
              }`}
            >
              {t.label}
              <span
                className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${
                  active ? 'bg-[#DBEAFE] text-[#1F6FEB]' : 'bg-[#EBEDF0] text-[#475569]'
                }`}
              >
                {t.count}
              </span>
            </button>
          )
        })}
      </div>

      {/* KPI cards — server-side totals, narrowed client-side when a date chip is active */}
      <div className='mb-6 hidden min-[435px]:flex gap-4'>
        <KpiCard label='New' value={renderKpiCounts.newCount} icon={Inbox} accent='blue' />
        <KpiCard label='Reviewed' value={renderKpiCounts.reviewedCount} icon={CheckCircle2} accent='green' />
        <KpiCard label='Quoted' value={renderKpiCounts.quoteSentCount ?? 0} icon={FileText} accent='navy' />
      </div>

      {/* Date range chip strip (CAR-3450) */}
      <div className='mb-3 hidden min-[435px]:flex flex-wrap items-center gap-2'>
        {DATE_FILTER_OPTIONS.map((opt) => {
          const active = dateFilter === opt.value
          return (
            <button
              key={opt.value}
              type='button'
              onClick={() => applyDateFilter(opt.value)}
              className={`inline-flex items-center rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition-colors ${
                active ? 'bg-[#0A1628] text-white' : 'border border-[#D1D5D8] bg-white text-[#0A1628] hover:bg-[#F8FAFC]'
              }`}
              aria-pressed={active}
            >
              {opt.label}
            </button>
          )
        })}
      </div>

      {/* Filters */}
      <div className='mb-4 hidden min-[435px]:flex flex-wrap items-center gap-3'>
        <div className='relative min-w-[260px] flex-1'>
          <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]' />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder='Search by RO#, VIN, Vehicle or Client...'
            className='pl-9'
          />
        </div>
        <Select value={filters.client || ANY} onValueChange={(v) => setFilters({ client: v === ANY ? '' : v })}>
          <SelectTrigger className='w-[200px]'>
            <SelectValue placeholder='All Clients' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All Clients</SelectItem>
            {clientOptions.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.status || ANY} onValueChange={(v) => setFilters({ status: v === ANY ? '' : v })}>
          <SelectTrigger className='w-[180px]'>
            <SelectValue placeholder='All Statuses' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All Statuses</SelectItem>
            <SelectItem value='NEW'>New</SelectItem>
            <SelectItem value='REVIEWED'>Reviewed</SelectItem>
            <SelectItem value='QUOTE_SENT'>Quote Sent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Error banner */}
      {listError && (
        <div className='mb-3 flex items-center gap-2 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B91C1C]'>
          <AlertCircle className='h-4 w-4' />
          {listError}
          <button onClick={() => loadEstimates()} className='ml-auto text-[#B91C1C] underline'>
            Retry
          </button>
        </div>
      )}

      {bulkDownloadError && (
        <div className='mb-3 flex items-center gap-2 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B91C1C]'>
          <AlertCircle className='h-4 w-4' />
          {bulkDownloadError}
          <button onClick={() => setBulkDownloadError(null)} className='ml-auto text-[#B91C1C] underline'>
            Dismiss
          </button>
        </div>
      )}

      {bulkMarkReviewedError && (
        <div className='mb-3 flex items-center gap-2 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-sm text-[#B91C1C]'>
          <AlertCircle className='h-4 w-4' />
          {bulkMarkReviewedError}
          <button onClick={() => setBulkMarkReviewedError(null)} className='ml-auto text-[#B91C1C] underline'>
            Dismiss
          </button>
        </div>
      )}

      {/* Contextual action bar — visible when rows are selected */}
      {selectedIds.size > 0 && (
        <div className='mb-3 flex h-12 items-center justify-between rounded-lg border border-[#D1D5D8] bg-white px-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]'>
          <div className='flex items-center gap-3'>
            <span className='text-[13px] font-semibold text-[#0A1628]'>{selectedIds.size} selected</span>
            <button onClick={clearSelection} aria-label='Clear selection' className='text-[#6B7280] hover:opacity-70'>
              <X className='h-4 w-4' />
            </button>
          </div>
          <div className='flex items-center gap-2'>
            {canCreateQuotes && (
              <button
                onClick={() => setBulkQuoteOpen(true)}
                className='inline-flex h-9 items-center gap-2 rounded-lg bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90'
              >
                <FileText className='h-4 w-4' />
                Create {selectedIds.size} {selectedIds.size === 1 ? 'Quote' : 'Quotes'}
              </button>
            )}
            {/* Marking reviewed writes; a client login only reads and downloads (CAR-4055). */}
            {!isReadOnly && (
              <button
                onClick={onBulkMarkReviewed}
                disabled={isBulkMarkingReviewed}
                className='inline-flex h-9 items-center gap-2 rounded-lg border border-[#D1D5D8] bg-white px-4 text-sm text-[#0A1628] transition-colors hover:bg-[#F8FAFC] disabled:opacity-60 disabled:pointer-events-none'
              >
                {isBulkMarkingReviewed ? <Loader2 className='h-4 w-4 animate-spin' /> : <CheckCircle2 className='h-4 w-4' />}
                Mark Reviewed
              </button>
            )}
            <button
              onClick={onBulkDownload}
              disabled={isBulkDownloading}
              className='inline-flex h-9 items-center gap-2 rounded-lg border border-[#D1D5D8] bg-white px-4 text-sm text-[#0A1628] transition-colors hover:bg-[#F8FAFC] disabled:opacity-60 disabled:pointer-events-none'
            >
              {isBulkDownloading ? <Loader2 className='h-4 w-4 animate-spin' /> : <Download className='h-4 w-4' />}
              Download PDFs
            </button>
          </div>
        </div>
      )}

      {/* Mobile card list */}
      <div className='min-[435px]:hidden space-y-3'>
        {isListLoading && visibleEstimates.length === 0 ? (
          <div className='flex flex-col items-center justify-center rounded-xl border border-[#E5E7EB] bg-white py-10'>
            <Loader2 className='h-5 w-5 animate-spin text-[#1F6FEB]' />
            <div className='mt-2 text-[13px] text-[#6B7280]'>Loading estimates...</div>
          </div>
        ) : visibleEstimates.length === 0 ? (
          <div className='rounded-xl border border-[#E5E7EB] bg-white p-8 text-center text-[13px] text-[#6B7280]'>
            {estimates.length === 0 ? emptyListText : 'No estimates match the selected filters.'}
          </div>
        ) : (
          visibleEstimates.map((e) => {
            const pill = statusPillStyle(e.status)
            return (
              <div
                key={e.id || e.ro}
                onClick={() => {
                  const target = e.id || e.ro
                  if (target) history.push(`/adas-intelligence/estimate/${target}`)
                }}
                className='cursor-pointer rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm transition-colors hover:bg-[#F8FAFC]'
              >
                <div className='flex items-start justify-between gap-3'>
                  <div className='flex items-center gap-1 text-[12px] text-[#475569]'>
                    RO #{e.ro}
                    <EstimateLockIcon locked={e.estimateLocked} />
                    {e.planLocked && <PlanLockedPill className='ml-1' />}
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${pill.bg} ${pill.text}`}
                  >
                    <span className={`inline-block h-1.5 w-1.5 rounded-full ${pill.dot}`} />
                    <span className='capitalize'>{e.status === 'quote sent' ? 'Quoted' : e.status}</span>
                  </span>
                </div>
                <div className='mt-1 text-[16px] font-bold uppercase leading-tight tracking-tight text-[#0A1628]'>{e.vehicle || '—'}</div>
                {e.client && e.client !== '—' && (
                  <div className='mt-0.5 text-[13px] text-[#475569]'>
                    <span>{e.client}</span>
                    {e.insurance && (
                      <>
                        <span className='mx-1.5 text-[#CBD5E1]'>·</span>
                        <span>{e.insurance}</span>
                      </>
                    )}
                  </div>
                )}
                {e.vin && <div className='mt-1 font-mono text-[11px] tracking-wider text-[#475569]'>VIN {e.vin}</div>}
                {!e.record?.number && e.autoQuote?.inProgress && (
                  <div className='mt-1 inline-flex items-center gap-1.5 text-[12px] font-medium text-[#1F6FEB]'>
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                    Auto-quoting…
                  </div>
                )}
                <hr className='my-3 border-[#EBEDF0]' />
                <div className='flex items-center justify-between gap-2'>
                  <div className='inline-flex items-center gap-1.5 text-[13px] text-[#1F6FEB]'>
                    <Clock className='h-3.5 w-3.5' />
                    <span>
                      {e.calibrationsCount ?? 0} {e.calibrationsCount === 1 ? 'calibration' : 'calibrations'}
                    </span>
                  </div>
                  <div className='text-[12px] text-[#475569]'>Created {formatDateTime(e.dateReceivedIso) || e.dateReceived}</div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Mobile floating "+ Upload" CTA */}
      {!isReadOnly && (
        <button
          onClick={() => setUploadOpen(true)}
          className='min-[435px]:hidden fixed bottom-5 right-5 z-30 inline-flex items-center gap-1.5 rounded-full bg-sidebar px-5 py-3 text-sm font-semibold text-white shadow-lg hover:bg-sidebar/90'
        >
          <Plus className='h-4 w-4' />
          Upload
        </button>
      )}

      {/* Table */}
      <div className='hidden min-[435px]:block overflow-hidden rounded-lg border border-[#D1D5D8] bg-white'>
        <Table>
          <TableHeader>
            <TableRow className='bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] hover:from-[#2C3D4D] hover:to-[#1F2D3D]'>
              <TableHead className='w-10 text-white' aria-label='Selection' />
              {ESTIMATE_SORT_OPTIONS.map((o) => (
                <SortableTableHead
                  key={o.value}
                  label={o.label}
                  sortKey={o.value}
                  sort={sort}
                  onSort={(key) => setSort(nextSort(sort, key))}
                  className='text-white'
                />
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isListLoading && visibleEstimates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className='py-12 text-center'>
                  <Loader2 className='mx-auto h-5 w-5 animate-spin text-[#1F6FEB]' />
                  <div className='mt-2 text-sm text-[#6B7280]'>Loading estimates...</div>
                </TableCell>
              </TableRow>
            ) : visibleEstimates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className='py-10 text-center text-sm text-[#6B7280]'>
                  {estimates.length === 0 ? emptyListText : 'No estimates match the selected date range.'}
                </TableCell>
              </TableRow>
            ) : (
              visibleEstimates.map((e) => {
                const isSelected = !!(e.id && selectedIds.has(e.id))
                return (
                  <TableRow
                    key={e.id || e.ro}
                    data-state={isSelected ? 'selected' : undefined}
                    className='cursor-pointer h-14 even:bg-table-row-even hover:bg-table-row-hover data-[state=selected]:bg-[#E8EFFF] data-[state=selected]:hover:bg-[#E8EFFF]'
                    onClick={() => e.id && history.push(`/adas-intelligence/estimate/${e.id}`)}
                  >
                    <TableCell className='align-middle' onClick={(evt) => evt.stopPropagation()}>
                      <Checkbox
                        aria-label={
                          e.record?.href ? `Already linked to ${e.record.type || 'record'} #${e.record.number}` : `Select ${e.ro}`
                        }
                        title={e.record?.href ? 'This estimate is already linked to a quote/work order/invoice.' : undefined}
                        checked={e.id ? selectedIds.has(e.id) : false}
                        onCheckedChange={() => e.id && !e.record?.href && toggleRow(e.id)}
                        disabled={!e.id || !!e.record?.href}
                      />
                    </TableCell>
                    <TableCell className='align-middle font-mono font-medium text-[#0A1628]'>
                      <span className='inline-flex items-center gap-1.5'>
                        {e.ro}
                        <EstimateLockIcon locked={e.estimateLocked} />
                      </span>
                    </TableCell>
                    <TableCell className='align-middle text-[#0A1628]'>
                      <div className='max-w-[180px] leading-snug line-clamp-2'>{e.client}</div>
                    </TableCell>
                    <TableCell className='align-middle text-[#0A1628]'>
                      {e.insurance ? (
                        <div className='max-w-[160px] truncate' title={e.insurance}>
                          {e.insurance}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className='align-middle text-[#0A1628]'>
                      <div className='max-w-[200px] leading-snug line-clamp-2'>{e.vehicle}</div>
                    </TableCell>
                    <TableCell className='align-middle font-mono text-[13px] text-[#475569]'>
                      {e.vin || <span className='text-[#6B7280]'>—</span>}
                    </TableCell>
                    <TableCell className='align-middle text-[13px] text-[#475569]'>{e.dateReceived}</TableCell>
                    <TableCell className='align-middle'>
                      <StatusPill status={e.status} />
                    </TableCell>
                    <TableCell className='align-middle'>
                      {e.planLocked ? <LockedValue>12</LockedValue> : <CalibrationsCell count={e.calibrationsCount} />}
                    </TableCell>
                    <TableCell className='align-middle' onClick={(evt) => evt.stopPropagation()}>
                      {e.planLocked ? (
                        <PlanLockedPill />
                      ) : (
                        <RecordLink record={e.record} autoQuote={e.autoQuote} onNavigate={(href) => history.push(href)} />
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination footer */}
      <div className='mt-3 hidden min-[435px]:flex items-center justify-between'>
        <div className='text-[12px] text-[#6B7280]'>
          {totalElements > 0
            ? `Showing ${page * size + 1}\u2013${Math.min((page + 1) * size, totalElements)} of ${totalElements}`
            : 'No results'}
        </div>
        <div className='flex items-center gap-1'>
          <Button variant='outline' size='sm' disabled={page <= 0 || isListLoading} onClick={() => setPage(Math.max(0, page - 1))}>
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <span className='px-2 text-[12px] text-[#6B7280]'>
            Page {page + 1} / {Math.max(totalPages, 1)}
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={page >= totalPages - 1 || isListLoading}
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          >
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
      </div>

      {/* Mobile filter bottom drawer — Client / Status / Date Range selects */}
      {filterOpen && (
        <div className='fixed inset-0 z-40 flex flex-col justify-end' role='dialog' aria-modal='true'>
          <div className='absolute inset-0 bg-black/50' onClick={closeMobileToolbar} />
          <div className='relative z-10 flex max-h-[88vh] flex-col rounded-t-2xl bg-white'>
            <div className='flex items-center justify-between border-b border-[#EBEDF0] px-5 py-4'>
              <div className='text-[18px] font-bold text-[#0A1628]'>Filters</div>
              <div className='flex items-center gap-3'>
                <button onClick={clearMobileFilters} className='text-[14px] font-semibold text-[#1F6FEB] hover:underline'>
                  Clear
                </button>
                <button
                  onClick={closeMobileToolbar}
                  aria-label='Close filters'
                  className='inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#F1F4F8] text-[#475569]'
                >
                  <X className='h-4 w-4' />
                </button>
              </div>
            </div>
            <div className='flex-1 overflow-y-auto px-5 py-4'>
              <div className='mb-4'>
                <MobileSortControl options={ESTIMATE_SORT_OPTIONS} sort={draftSort} onChange={setDraftSort} />
              </div>
              <div className='mb-4'>
                <div className='mb-1.5 text-[11px] font-bold uppercase tracking-wide text-[#0A1628]'>Client</div>
                <select
                  value={draftClient}
                  onChange={(e) => setDraftClient(e.target.value)}
                  className='block w-full rounded-lg border border-[#D1D5D8] bg-white px-3 py-2.5 text-[14px] text-[#0A1628] focus:border-[#1F6FEB] focus:outline-none'
                >
                  <option value=''>All Clients</option>
                  {clientOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className='mb-4'>
                <div className='mb-1.5 text-[11px] font-bold uppercase tracking-wide text-[#0A1628]'>Status</div>
                <select
                  value={draftStatus}
                  onChange={(e) => setDraftStatus(e.target.value)}
                  className='block w-full rounded-lg border border-[#D1D5D8] bg-white px-3 py-2.5 text-[14px] text-[#0A1628] focus:border-[#1F6FEB] focus:outline-none'
                >
                  <option value=''>All Statuses</option>
                  <option value='NEW'>New</option>
                  <option value='REVIEWED'>Reviewed</option>
                  <option value='QUOTE_SENT'>Quote Sent</option>
                </select>
              </div>
              <div className='mb-4'>
                <div className='mb-1.5 text-[11px] font-bold uppercase tracking-wide text-[#0A1628]'>Date Range</div>
                <select
                  value={draftDate}
                  onChange={(e) => setDraftDate(e.target.value)}
                  className='block w-full rounded-lg border border-[#D1D5D8] bg-white px-3 py-2.5 text-[14px] text-[#0A1628] focus:border-[#1F6FEB] focus:outline-none'
                >
                  {DATE_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className='border-t border-[#EBEDF0] px-5 py-4'>
              <button
                onClick={applyMobileFilters}
                className='w-full rounded-xl bg-sidebar py-3.5 text-[15px] font-semibold text-white hover:bg-sidebar/90'
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <UploadEstimateModal
        open={uploadOpen}
        onOpenChange={(next) => {
          setUploadOpen(next)
          if (!next) loadEstimates()
        }}
      />

      <BulkQuoteModal
        open={bulkQuoteOpen}
        onOpenChange={(next) => {
          setBulkQuoteOpen(next)
          if (!next) setBulkQuoteError(null)
        }}
        selectedEstimates={selectedEstimates}
        onConfirm={onBulkCreateQuotes}
        isSubmitting={isCreatingBulk}
        error={bulkQuoteError}
        clients={clients}
        insuranceCompanies={insuranceCompanies}
        defaults={bulkDefaults}
        isPreparing={isPreparingBulk}
      />

      <FormModal
        open={!!errorDialog}
        onClose={() => setErrorDialog(null)}
        icon={AlertCircle}
        title={errorDialog?.title || 'Error'}
        width='max-w-[480px]'
        clientMayEdit
        footer={<FormModalFooter onConfirm={() => setErrorDialog(null)} confirmLabel='Close' />}
      >
        {errorDialog?.message && <p className='text-[13px] leading-5 text-[#334155]'>{errorDialog.message}</p>}
        {errorDialog?.items?.length > 0 && (
          <div className='mt-2 max-h-72 overflow-auto'>
            {errorDialog.items.map((item, i) => (
              <div
                key={`${item.ro}-${i}`}
                className='flex items-start justify-between gap-3 py-2 text-[13px]'
                style={{ borderBottom: i < errorDialog.items.length - 1 ? '1px solid #E8ECF1' : 'none' }}
              >
                <span className='font-semibold text-[#0A1628]'>RO {item.ro}</span>
                <span className='text-[#B91C1C] text-right'>{item.message}</span>
              </div>
            ))}
          </div>
        )}
      </FormModal>
    </div>
  )
}
